import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { CURRENT_APP_VERSION, clearDataMigration } from '@/utils/zustand_helpers';

/**
 * @deprecated Legacy "distance from liquidation price" model. The same percentage meant
 * different risk at different leverage (see {@link evaluateLiquidationAlerts}), so it was
 * replaced by the margin-buffer model below. Retained only so an upgrading trader's prior
 * value survives the migration; nothing reads it anymore.
 * export const LIQUIDATION_ALERT_THRESHOLD_CHOICES = [1, 2, 3, 5, 10] as const;
 */
export const LIQUIDATION_ALERT_COOLDOWN_CHOICES = [5, 15, 30, 60, 120, 720, 1440] as const;

/**
 * Current model: alert once an adverse move has consumed this share of the position's
 * margin buffer — the entry→liquidation price span, which equals (IMR − MMR) and is set by
 * leverage. Normalizing by that span makes a given step mean the same risk on a 2× and a 50×.
 */
export const LIQUIDATION_ALERT_MARGIN_USED_CHOICES = [25, 50, 70, 85, 95] as const;

export const DEFAULT_LIQUIDATION_ALERT_ENABLED = true;
/** @deprecated default for the legacy distance-from-liquidation field; kept for migration only. */
export const DEFAULT_LIQUIDATION_ALERT_THRESHOLD_PERCENT = 5;
export const DEFAULT_LIQUIDATION_ALERT_MARGIN_USED_PERCENT = 50;
export const DEFAULT_LIQUIDATION_ALERT_COOLDOWN_MINUTES = 60;

/**
 * Leverage assumed when converting a legacy "distance from liquidation price" threshold to
 * the new margin-buffer model. We use the platform's top leverage (the smallest buffer) on
 * purpose: it's the conservative end, so for any trader at or below it the migrated alert
 * fires at least as early as their old one did, never later.
 */
export const LEGACY_THRESHOLD_ASSUMED_LEVERAGE = 10;

/**
 * Map a legacy distance-from-liquidation % to the closest margin-used % step.
 *
 *   marginUsed = 1 − oldThreshold / IMR,  IMR = 1 / assumedLeverage
 *
 * The buffer width is leverage-dependent and not stored per setting, so this is an estimate
 * (see {@link LEGACY_THRESHOLD_ASSUMED_LEVERAGE}); we snap to an offered step so the Settings
 * dropdown can render the result. Exact at the assumed leverage — e.g. 5% → 50%.
 */
export function convertLegacyThresholdToMarginUsed(
  oldThresholdPercent: number,
  choices: readonly number[] = LIQUIDATION_ALERT_MARGIN_USED_CHOICES
): number {
  const imr = 1 / LEGACY_THRESHOLD_ASSUMED_LEVERAGE; // entry→liq buffer as a fraction of price
  const rawPercent = (1 - oldThresholdPercent / 100 / imr) * 100;
  // Snap to the closest step; on an exact tie the strict `<` keeps the earlier (lower) step,
  // i.e. the earlier-firing / safer one — assumes `choices` is ascending.
  return choices.reduce((best, choice) => (Math.abs(choice - rawPercent) < Math.abs(best - rawPercent) ? choice : best));
}

type LiquidationAlertsState = {
  enabled: boolean;
  /** @deprecated legacy distance-from-liquidation threshold; retained for migration, not read by the system. */
  thresholdPercent: number;
  /** Source of truth: % of the position's margin buffer consumed before alerting. */
  marginUsedPercent: number;
  cooldownMinutes: number;
  lastNotifiedAt: Record<string, number>;
  // actions
  setEnabled: (enabled: boolean) => void;
  /** @deprecated no UI surfaces this; kept for back-compat only. */
  setThresholdPercent: (thresholdPercent: number) => void;
  setMarginUsedPercent: (marginUsedPercent: number) => void;
  setCooldownMinutes: (cooldownMinutes: number) => void;
  resetPreferences: () => void;
  markNotified: (positionKey: string, at: number) => void;
  retainPositions: (currentKeys: Set<string>) => void;
  clearState: () => void;
};

export const useLiquidationAlertsStore = create<LiquidationAlertsState>()(
  persist(
    (set) => ({
      enabled: DEFAULT_LIQUIDATION_ALERT_ENABLED,
      thresholdPercent: DEFAULT_LIQUIDATION_ALERT_THRESHOLD_PERCENT,
      marginUsedPercent: DEFAULT_LIQUIDATION_ALERT_MARGIN_USED_PERCENT,
      cooldownMinutes: DEFAULT_LIQUIDATION_ALERT_COOLDOWN_MINUTES,
      lastNotifiedAt: {},

      setEnabled: (enabled) => set({ enabled }),
      setThresholdPercent: (thresholdPercent) => set({ thresholdPercent }),
      setMarginUsedPercent: (marginUsedPercent) => set({ marginUsedPercent }),
      setCooldownMinutes: (cooldownMinutes) => set({ cooldownMinutes }),

      resetPreferences: () =>
        set({
          enabled: DEFAULT_LIQUIDATION_ALERT_ENABLED,
          thresholdPercent: DEFAULT_LIQUIDATION_ALERT_THRESHOLD_PERCENT,
          marginUsedPercent: DEFAULT_LIQUIDATION_ALERT_MARGIN_USED_PERCENT,
          cooldownMinutes: DEFAULT_LIQUIDATION_ALERT_COOLDOWN_MINUTES,
        }),

      markNotified: (positionKey, at) => set((state) => ({ lastNotifiedAt: { ...state.lastNotifiedAt, [positionKey]: at } })),

      retainPositions: (currentKeys) =>
        set((state) => {
          let changed = false;
          const next: Record<string, number> = {};
          for (const [key, at] of Object.entries(state.lastNotifiedAt)) {
            if (currentKeys.has(key)) {
              next[key] = at;
            } else {
              changed = true;
            }
          }
          return changed ? { lastNotifiedAt: next } : {};
        }),

      clearState: () => set({ lastNotifiedAt: {} }),
    }),
    {
      name: 'dex-liquidation-alerts',
      version: CURRENT_APP_VERSION,
      migrate: (persistedState, version) => {
        const state = clearDataMigration(persistedState, version);
        // Pre-margin-model installs persisted only `thresholdPercent` (the old distance-from-
        // liquidation setting). We keep that value intact (reference / rollback) and derive the
        // new field from it via convertLegacyThresholdToMarginUsed — an estimate at the platform's
        // top leverage, which is the conservative end (alerts fire no later than before).
        if (state && typeof state === 'object' && state.marginUsedPercent == null) {
          return {
            ...state,
            marginUsedPercent:
              typeof state.thresholdPercent === 'number'
                ? convertLegacyThresholdToMarginUsed(state.thresholdPercent)
                : DEFAULT_LIQUIDATION_ALERT_MARGIN_USED_PERCENT,
          };
        }
        return state;
      },
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        enabled: state.enabled,
        thresholdPercent: state.thresholdPercent,
        marginUsedPercent: state.marginUsedPercent,
        cooldownMinutes: state.cooldownMinutes,
        lastNotifiedAt: state.lastNotifiedAt,
      }),
    }
  )
);
